import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiGrid,
  FiCreditCard,
  FiShield,
  FiBriefcase,
  FiHelpCircle,
  FiSun,
  FiMoon,
  FiExternalLink,
  FiLogOut,
  FiMoreHorizontal,
  FiX,
  FiTrendingUp,
  FiSettings,
  FiUsers,
  FiAward,
  FiPercent,
  FiFileText,
  FiCalendar,
  FiUserCheck,
  FiInbox,
  FiImage,
  FiDollarSign,
  FiGift,
} from 'react-icons/fi';

import styles from './AdminShell.module.css';
import NotificationBell from '../NotificationBell/NotificationBell';
import { useTheme } from '../../app/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { logout } from '../../features/auth/authSlice';

const NAV_ITEMS = [
  { label: 'Overview', path: '/admin', Icon: FiGrid },
  { label: 'All Requests', path: '/admin/all-requests', Icon: FiInbox },
  { label: 'Users', path: '/admin/users', Icon: FiUsers },
  { label: 'Payments', path: '/admin/payments', Icon: FiTrendingUp },
  {
    label: 'Portal Investments',
    path: '/admin/portal-investments',
    Icon: FiPercent,
  },
  { label: 'Funds', path: '/admin/funds', Icon: FiCreditCard },
];

const MORE_ITEMS = [
  {
    label: 'BCT100 Claims',
    path: '/admin/bct-token-claims',
    Icon: FiGift,
  },
  { label: 'Leads', path: '/admin/leads', Icon: FiUserCheck },
  { label: 'KYC review', path: '/admin/kyc', Icon: FiShield },
  { label: 'Meetings', path: '/admin/meetings', Icon: FiCalendar },
  { label: 'KYB review', path: '/admin/kyb', Icon: FiBriefcase },
  { label: 'Support', path: '/admin/support', Icon: FiHelpCircle },
  { label: 'Reward Report', path: '/admin/rewards', Icon: FiAward },
  { label: 'Contest & Prizes', path: '/admin/contest', Icon: FiAward },
  {
    label: 'Partner Applications',
    path: '/admin/partners',
    Icon: FiAward,
  },
  {
    label: 'Documents',
    path: '/admin/company-documents',
    Icon: FiFileText,
  },
  { label: 'Landing Content', path: '/admin/content', Icon: FiImage },
  { label: 'Currencies', path: '/admin/currencies', Icon: FiDollarSign },
  { label: 'Ranks & Brand Club', path: '/admin/ranks', Icon: FiAward },
  { label: 'System Settings', path: '/admin/settings', Icon: FiSettings },
];

const ALL_NAV = [
  { label: 'Overview', path: '/admin', Icon: FiGrid },
  { label: 'All Requests', path: '/admin/all-requests', Icon: FiInbox },
  { label: 'Leads', path: '/admin/leads', Icon: FiUserCheck },
  { label: 'Users', path: '/admin/users', Icon: FiUsers },
  { label: 'Payments', path: '/admin/payments', Icon: FiTrendingUp },
  {
    label: 'Portal Investments',
    path: '/admin/portal-investments',
    Icon: FiPercent,
  },
  {
    label: 'BCT100 Claims',
    path: '/admin/bct-token-claims',
    Icon: FiGift,
  },
  { label: 'Funds', path: '/admin/funds', Icon: FiCreditCard },
  { label: 'KYC review', path: '/admin/kyc', Icon: FiShield },
  { label: 'Meetings', path: '/admin/meetings', Icon: FiCalendar },
  { label: 'KYB review', path: '/admin/kyb', Icon: FiBriefcase },
  { label: 'Support', path: '/admin/support', Icon: FiHelpCircle },
  { label: 'Reward Report', path: '/admin/rewards', Icon: FiAward },
  { label: 'Contest & Prizes', path: '/admin/contest', Icon: FiAward },
  {
    label: 'Partner Applications',
    path: '/admin/partners',
    Icon: FiAward,
  },
  {
    label: 'Documents',
    path: '/admin/company-documents',
    Icon: FiFileText,
  },
  { label: 'Landing Content', path: '/admin/content', Icon: FiImage },
  { label: 'Currencies', path: '/admin/currencies', Icon: FiDollarSign },
  { label: 'Ranks & Brand Club', path: '/admin/ranks', Icon: FiAward },
  { label: 'System Settings', path: '/admin/settings', Icon: FiSettings },
];

interface Props {
  children: React.ReactNode;
}

const AdminShell: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = () => {
    setMoreOpen(false);
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path: string) =>
    location.pathname === path
    || (
      path !== '/admin'
      && location.pathname.startsWith(`${path}/`)
    );

  const isMoreActive = MORE_ITEMS.some((item) => isActive(item.path));

  return (
    <div className={styles.root} data-theme={theme}>
      <div className={styles.mobileTopBar}>
        <Link to="/" className={styles.sidebarBrand}>
          <img
            src="/images/logo.png"
            alt="BCT"
            className={styles.sidebarLogo}
          />
          <span>BCT Platform</span>
        </Link>

        <NotificationBell />
      </div>

      <div className={styles.shell}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarTop}>
            <Link to="/" className={styles.sidebarBrand}>
              <img
                src="/images/logo.png"
                alt="BCT"
                className={styles.sidebarLogo}
              />
              <span>BCT Platform</span>
            </Link>

            <NotificationBell />
          </div>

          <div className={`${styles.navList} thinScroll`}>
            {ALL_NAV.map((item) => {
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  className={`${styles.navItem} ${
                    active ? styles.navItemActive : ''
                  }`}
                >
                  <item.Icon />
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className={styles.sidebarFooter}>
            <div className={styles.iconCol}>
              <Link to="/" className={styles.iconBtn}>
                <FiExternalLink />
                <span>Website</span>
              </Link>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <FiSun /> : <FiMoon />}
                <span>
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </span>
              </button>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={handleLogout}
              >
                <FiLogOut />
                <span>Logout</span>
              </button>
            </div>

            {user && (
              <div className={styles.profileCard}>
                <div className={styles.profileAvatar}>
                  {user.full_name?.[0]?.toUpperCase() || '?'}
                </div>

                <div className={styles.profileInfo}>
                  <div className={styles.profileName}>
                    {user.full_name}
                  </div>
                  <div className={styles.profileRole}>
                    {user.role}
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>

        <div className={styles.content}>{children}</div>
      </div>

      <nav className={styles.bottomNav} aria-label="Admin navigation">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className={`${styles.bottomNavItem} ${
                active ? styles.bottomNavItemActive : ''
              }`}
            >
              <item.Icon />
              <span>{item.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          className={`${styles.bottomNavItem} ${
            isMoreActive ? styles.bottomNavItemActive : ''
          }`}
          aria-expanded={moreOpen}
          aria-controls="admin-more-menu"
          onClick={() => setMoreOpen(true)}
        >
          <FiMoreHorizontal />
          <span>More</span>
        </button>
      </nav>

      {moreOpen && (
        <div
          className={styles.sheetOverlay}
          onClick={() => setMoreOpen(false)}
        >
          <div
            id="admin-more-menu"
            className={styles.sheet}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.sheetHandle} />

            <div className={styles.sheetHeader}>
              <span>Menu</span>

              <button
                type="button"
                className={styles.sheetClose}
                aria-label="Close admin menu"
                onClick={() => setMoreOpen(false)}
              >
                <FiX />
              </button>
            </div>

            {user && (
              <div
                className={styles.profileCard}
                style={{ marginBottom: 14 }}
              >
                <div className={styles.profileAvatar}>
                  {user.full_name?.[0]?.toUpperCase() || '?'}
                </div>

                <div className={styles.profileInfo}>
                  <div className={styles.profileName}>
                    {user.full_name}
                  </div>
                  <div className={styles.profileRole}>
                    {user.role}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.sheetGrid}>
              {MORE_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={styles.sheetItem}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  onClick={() => setMoreOpen(false)}
                >
                  <item.Icon />
                  {item.label}
                </Link>
              ))}
            </div>

            <div
              className={styles.iconCol}
              style={{ marginTop: 14 }}
            >
              <Link
                to="/"
                className={styles.iconBtn}
                onClick={() => setMoreOpen(false)}
              >
                <FiExternalLink />
                <span>Website</span>
              </Link>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <FiSun /> : <FiMoon />}
                <span>
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </span>
              </button>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={handleLogout}
              >
                <FiLogOut />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShell;